import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import Reveal from "@/components/Reveal";
import GoldCorners from "@/components/GoldCorners";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/s0rm07o0_IMG_2192.png";

const BLOCK_IMAGES = [
  "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1600&q=85",
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=85",
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/dtddvasc_IMG_2190.png",
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/c7itkshs_9D73C893-6699-46EE-A6BA-C73A4AAB96A3.png",
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/lc4fbwjf_e00e352c-2e3b-4273-b932-cbc6351e93a8.jpeg",
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/nvakaqkb_IMG_2114.png",
  "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1600&q=85",
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/2jorp346_3deaafb0-cec3-499e-825e-ffb30dc3fad8.jpeg",
];

function Block({ b, img, index }) {
  const reverse = index % 2 === 1;
  return (
    <section className={`relative py-20 sm:py-28 ${index > 0 ? "border-t border-[#C7A26C]/15" : ""}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <Reveal className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
          <div className="relative">
            <div aria-hidden className="absolute -inset-6 sm:-inset-8 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(26, 80, 115, 0.35), rgba(15, 44, 64, 0.18) 45%, transparent 75%)", filter: "blur(24px)" }} />
            <div aria-hidden className="absolute -inset-4 pointer-events-none" style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(199,162,108,0.10), transparent 55%)", filter: "blur(20px)" }} />
            <div className="relative aspect-[4/5] sm:aspect-[16/11] overflow-hidden" style={{ boxShadow: "0 30px 60px -20px rgba(18, 60, 90, 0.55), 0 14px 28px -10px rgba(7, 14, 19, 0.6)" }}>
              <img src={img} alt={b.t} className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.78) contrast(1.08) saturate(1.05)" }} />
              <div className="absolute inset-0 mix-blend-overlay opacity-55" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.28) 0%, rgba(168,132,90,0.18) 45%, rgba(7,14,19,0.45) 100%)" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070E13]/75 via-[#070E13]/15 to-transparent" />
              <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 120px 20px rgba(7,14,19,0.55)" }} />
              <GoldCorners size={14} inset={-2} />
            </div>
          </div>
        </Reveal>
        <Reveal delay={150} className={`lg:col-span-6 ${reverse ? "lg:order-1 lg:pe-8" : "lg:ps-8"}`}>
          <p className="overline gold-text">{b.eyebrow}</p>
          <h2 className="h-editorial text-4xl sm:text-5xl mt-5 leading-[1.05]">{b.t}</h2>
          {b.intro && <p className="mt-6 text-[#D6D1C9] text-lg leading-relaxed">{b.intro}</p>}
          {b.body && <p className={`${b.intro ? "mt-4" : "mt-6"} text-[#D6D1C9]/85 leading-relaxed`}>{b.body}</p>}
          {b.bullets && (
            <ul className="mt-7 space-y-2.5">
              {b.bullets.map((bul, i) => (
                <li key={i} className="flex gap-3 text-[#D6D1C9]">
                  <span className="text-[#C7A26C] mt-2 leading-none">·</span>
                  <span className="leading-relaxed">{bul}</span>
                </li>
              ))}
            </ul>
          )}
        </Reveal>
      </div>
    </section>
  );
}

export default function Institutional() {
  const { t } = useLang();
  const ins = t.institutional;
  return (
    <div data-testid="page-institutional" className="bg-[#070E13] text-[#F4EEE8]">
      <section className="relative pt-40 pb-24 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 motif-grid opacity-25" />
        <div className="absolute inset-0 motif-hex opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#070E13]/40 to-[#070E13]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <p className="overline gold-text">{ins.heroEyebrow}</p>
            <h1 className="h-editorial text-5xl sm:text-6xl lg:text-7xl mt-5">{ins.heroTitle}</h1>
            <p className="mt-8 text-lg text-[#D6D1C9] leading-relaxed">{ins.heroLead}</p>
            <div className="mt-10 max-w-xl border-s border-[#C7A26C]/40 ps-6 lg:ps-8 py-1">
              <p className="text-[#E8E4D9] text-base leading-relaxed">{ins.heroNote}</p>
            </div>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-6">
            <div className="relative">
              <div aria-hidden className="absolute -inset-8 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(26, 80, 115, 0.40), rgba(15, 44, 64, 0.20) 45%, transparent 75%)", filter: "blur(28px)" }} />
              <div aria-hidden className="absolute -inset-4 pointer-events-none" style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(199,162,108,0.12), transparent 55%)", filter: "blur(20px)" }} />
              <div className="relative aspect-[4/5] sm:aspect-[16/11] overflow-hidden" style={{ boxShadow: "0 30px 60px -20px rgba(18, 60, 90, 0.55), 0 14px 28px -10px rgba(7, 14, 19, 0.6)" }}>
                <img src={HERO_IMG} alt={ins.heroTitle} className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.78) contrast(1.08) saturate(1.05)" }} />
                <div className="absolute inset-0 mix-blend-overlay opacity-55" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.28) 0%, rgba(168,132,90,0.18) 45%, rgba(7,14,19,0.45) 100%)" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070E13]/75 via-[#070E13]/15 to-transparent" />
                <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 120px 20px rgba(7,14,19,0.55)" }} />
                <GoldCorners size={14} inset={-2} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="border-t border-[#C7A26C]/15">
        {ins.blocks.map((b, i) => (
          <Block key={i} b={b} img={BLOCK_IMAGES[i]} index={i} />
        ))}
      </div>

      <section className="border-t border-[#C7A26C]/15 py-14">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-12 text-center">
          <p className="text-xs text-[#D6D1C9]/65 italic max-w-3xl mx-auto">{ins.confidentiality}</p>
        </div>
      </section>

      <section className="relative border-t border-[#C7A26C]/15 py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 motif-grid opacity-20" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <p className="overline gold-text">{ins.ctaEyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{ins.ctaTitle}</h2>
            <p className="mt-6 max-w-xl mx-auto text-[#D6D1C9] leading-relaxed">{ins.ctaLead}</p>
            <Link to="/contact" data-testid="institutional-cta" className="cta-premium inline-flex items-center gap-2 mt-10 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-4 text-xs uppercase tracking-[0.24em] font-medium">
              {ins.ctaBtn} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
